import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import {
    IMeetingFilterParams,
} from '../../../../../api/meetings.dao';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import {IZoomDAO, IZoomWebinarReportDTO} from '../../../../../api/zoom.dao';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { ReportType } from '../../../../../api/reports.dao';
import { MEETINGS, WEBINARS } from '../../../../../constants/api/zoom-api.constants';

class GenerateZoomReportViewModel {
    @mobx.observable
    public loading: boolean = false;
    @mobx.observable
    public reportsGenerating: boolean = false;
    @mobx.observable
    public reportsList: IZoomWebinarReportDTO[];
    @mobx.observable
    public checkedReportsList: IZoomWebinarReportDTO[] = [];
    public constructor(
        public readonly accountId: number,
        private readonly event: IEventDetailsDTO,
        private readonly intl: IntlShape,
        private closePopover: () => void,
        public reportType: ReportType,
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.fetchReports();
    }

    @mobx.action
    private fetchReports = async () => {
        this.loading = true;
        const reportsListRequestResult = await this.getReportsListRequestResult();
        this.handleReportsLoaded(reportsListRequestResult);
    };

    private getReportsListRequestResult = async (): Promise<IZoomWebinarReportDTO[]> => {
        const params: IMeetingFilterParams = {
            eventId: this.event.eventId,
            accountId: this.accountId,
            reportType: this.reportType === ReportType.ZOOM_MEETING_REGISTRATION_REPORT ? MEETINGS : WEBINARS
        };
        return this.zoomDAO.getZoomWebinarReports({
            ...params
        });
    };

    @mobx.action
    private handleReportsLoaded = (reports: IZoomWebinarReportDTO[]) => {
        this.reportsList = reports;
        this.loading = false;
    };

    public isAllReportsChecked = (): boolean => {
        return !!this.reportsList && !!this.reportsList.length && this.reportsList.length === this.checkedReportsList.length;
    };

    @mobx.action
    public handleCheckAllReports = (): void => {
        this.checkedReportsList = this.isAllReportsChecked() ? [] : [...this.reportsList];
    };

    @mobx.action
    public handleReportCheck = (reportWebinarId: string): void => {
        if (this.isReportChecked(reportWebinarId)) {
            this.checkedReportsList = this.checkedReportsList.filter(el => el.connectionId !== reportWebinarId);
        } else {
            this.checkedReportsList.push(this.reportsList.find(value => value.connectionId === reportWebinarId));
        }
    };

    public isReportChecked = (reportWebinarId: string): boolean => {
        return this.checkedReportsList.some(el => el.connectionId === reportWebinarId);
    };

    public getEventTimezone(): string {
        return this.event.timeZone;
    }

    @mobx.action
    public handleGenerateReports = async (): Promise<void> => {
        const reportType = this.reportType === ReportType.ZOOM_MEETING_REGISTRATION_REPORT ? MEETINGS : WEBINARS;
        const webinarIds = this.checkedReportsList.map(el => el.connectionId);
        this.reportsGenerating = true;
        const registrantErrorIds = [];
        const webinarErrorIds = [];
        for (const webinarId of webinarIds) {
            try {
                if (this.reportType === ReportType.ZOOM_WEBINAR_REGISTRATION) {
                    await this.zoomDAO.getWebinarRegistrationReport({ webinarId: webinarId, accountId: this.accountId, eventId: this.event.eventId });
                }
                if (this.reportType === ReportType.ZOOM_WEBINAR_ATTENDEE) {
                    await this.zoomDAO.getWebinarAttendeeReport({ webinarId: webinarId, accountId: this.accountId, eventId: this.event.eventId });
                }
                if (this.reportType === ReportType.ZOOM_MEETING_REGISTRATION_REPORT) {
                    await this.zoomDAO.getZoomMeetingRegistrationReport({ meetingId: webinarId, accountId: this.accountId, eventId: this.event.eventId });
                }
            } catch (e) {
                if (e.response.status === 412) {
                    registrantErrorIds.push(webinarId);
                } else {
                    webinarErrorIds.push(webinarId);
                }
            }
        }
        mobx.runInAction(() => {
            this.reportsGenerating = false;
            if (webinarErrorIds.length || registrantErrorIds.length) {
                let finalMessage = '';
                const noWebinarErrorMessage = `The reports for the following ${reportType} could not be generated. ` +
                    `Please ensure that the ${reportType === MEETINGS ? 'Meeting' : 'Webinar'} ID is valid, and try again.\n` +
                    webinarErrorIds.join('\n');

                const noRegistrantErrorMessage = `\nThe following ${reportType} do not have any Registrants yet:\n` +
                    registrantErrorIds.join('\n');

                if (webinarErrorIds.length) finalMessage += noWebinarErrorMessage;
                if (registrantErrorIds.length) finalMessage += noRegistrantErrorMessage;
                alertServerError('Error', finalMessage);
            }
        });
        this.trackMixPanel();
        this.closePopover();
    };

    public trackMixPanel = () => {
        const trackingEventName = this.reportType === ReportType.ZOOM_WEBINAR_REGISTRATION
            ? TRACKING_EVENTS.GENERATE_ZOOM_REGISTRATION_REPORT
            : CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.WEBINAR_CONNECTION_REPORT_ATTENDEE;
        this.trackingService.get().track(trackingEventName, {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': this.event.eventId,
            'Open From': OpenFrom.Reports
        });
    };
}

export default GenerateZoomReportViewModel;
